import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tabs,
  Tab,
  Tooltip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import { useSelector } from 'react-redux';
// Removed Redux auth selectors - now using useAuth() hook
import { useUserData } from '../../hooks/useUserData';
import { getCareTeam, setCareTeam } from '../../services/careCoordinationService';
import { getFacilityUsers } from '../../services/facilityUsersService';
import { npiRegistryService } from '../../services/npiRegistryService';
import { NPIProviderSearchField } from '../Procedures/components/NPIProviderSearchField';
import { UserRole, getEnumValues, USER_ROLE_DISPLAY_NAMES } from '../../types/enums';

const EMPTY_MEMBER = {
  name: '',
  role: '',
  specialty: '',
  phone: '',
  email: '',
  facility: '',
  coordinator: false,
  userId: '',
  source: '', // 'internal', 'external', or 'manual'
};

export default function CareTeam({ patientId, facilityId, saveToFirestore = true, onCareTeamChange }) {
  const [careTeam, setCareTeamState] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editIdx, setEditIdx] = useState(null);
  const [form, setForm] = useState(EMPTY_MEMBER);
  const [roleFilter, setRoleFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [dialogTab, setDialogTab] = useState(0); // 0: Internal, 1: External, 2: Manual
  const [npiSearch, setNpiSearch] = useState('');
  const [npiResults, setNpiResults] = useState([]);
  const [npiLoading, setNpiLoading] = useState(false);
  const npiSearchTimeout = useRef();
  
  // Get current user from hook
  const { user: currentUser } = useUserData();

  // Fetch care team for patient
  useEffect(() => {
    if (patientId) {
      getCareTeam(patientId).then(data => setCareTeamState(data?.members || []));
    }
  }, [patientId]);

  // Fetch users for facility using current user ID
  useEffect(() => {
    if (currentUser?.uid) {
      getFacilityUsers(currentUser.uid).then(users => {
        setUsers(users);
      });
    }
  }, [currentUser?.uid, facilityId]);

  // Filter users by selected role
  const filteredUsers = roleFilter
    ? users.filter(u => {
        // Get user roles from both possible sources
        const userRoles = u.roles || [];
        const legacyRole = u.role;
        
        // Combine roles into a single array, filtering out undefined/null values
        const allUserRoles = [...userRoles, legacyRole].filter(Boolean);
        
        // Check if the user has the selected role
        return allUserRoles.includes(roleFilter);
      })
    : users;

  // Open dialog for add/edit
  const handleOpen = (idx = null) => {
    setEditIdx(idx);
    setNpiSearch('');
    setNpiResults([]);
    setNpiLoading(false);
    if (idx !== null) {
      const member = careTeam[idx];
      setForm(member);
      setRoleFilter(member.role);
      // Set dialog tab based on member's source
      const sourceToTabMap = { 'internal': 0, 'external': 1, 'manual': 2 };
      setDialogTab(sourceToTabMap[member.source] || 0);
    } else {
      setForm(EMPTY_MEMBER);
      setRoleFilter('');
      setDialogTab(0); // Default to internal for new members
    }
    setOpen(true);
  };
  const handleClose = () => setOpen(false);

  // Handle form changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
    if (name === 'role') setRoleFilter(value);
  };

  // When user is selected, auto-fill name, specialty, contact, facility
  const handleUserSelect = (e) => {
    const userId = e.target.value;
    const user = users.find(u => u.id === userId);
    if (user) {
      setForm({
        ...form,
        userId,
        name: user.name || user.email,
        specialty: user.specialty || '',
        phone: user.phone || '',
        email: user.email || '',
        facility: user.facilityName || '',
      });
    }
  };

  // Save (add or edit)
  const handleSave = async () => {
    // Set source based on dialog tab
    const sourceMap = { 0: 'internal', 1: 'external', 2: 'manual' };
    const memberWithSource = { ...form, source: sourceMap[dialogTab] };
    
    let newTeam;
    if (editIdx !== null) {
      newTeam = [...careTeam];
      newTeam[editIdx] = memberWithSource;
    } else {
      newTeam = [...careTeam, memberWithSource];
    }
    setCareTeamState(newTeam);
    
    if (saveToFirestore) {
      setLoading(true);
      await setCareTeam(patientId, newTeam, 'system');
      setLoading(false);
      // Refresh care team from backend to ensure latest data
      if (patientId) {
        getCareTeam(patientId).then(data => setCareTeamState(data?.members || []));
      }
    }
    
    // Notify parent component of care team changes
    if (onCareTeamChange) {
      onCareTeamChange(newTeam);
    }
    
    // Do NOT close the dialog here
    // Optionally, reset the form for adding another member
    setForm(EMPTY_MEMBER);
    setRoleFilter('');
    setEditIdx(null);
  };

  // Delete
  const handleDelete = async (idx) => {
    const newTeam = careTeam.filter((_, i) => i !== idx);
    setCareTeamState(newTeam);
    
    if (saveToFirestore) {
      setLoading(true);
      await setCareTeam(patientId, newTeam, 'system');
      setLoading(false);
    }
    
    // Notify parent component of care team changes
    if (onCareTeamChange) {
      onCareTeamChange(newTeam);
    }
  };

  // NPI Search for External tab
  const handleNpiSearch = async () => {
    setNpiLoading(true);
    try {
      const results = await npiRegistryService.searchProvidersByName(undefined, npiSearch);
      setNpiResults(results);
    } catch (e) {
      setNpiResults([]);
    }
    setNpiLoading(false);
  };
  const handleNpiSelect = (provider) => {
    setForm({
      ...form,
      name: provider.basic
        ? (provider.basic.first_name
            ? `${provider.basic.first_name} ${provider.basic.last_name || ''}`.trim()
            : provider.basic.name || '')
        : '',
      specialty: provider.taxonomies?.[0]?.desc || '',
      phone: provider.addresses?.[0]?.telephone_number || '',
      email: provider.basic?.authorized_official_telephone_number || '',
      userId: '',
      facility: '',
      source: 'external', // Explicitly set source for NPI providers
    });
  };

  // Add debounce for NPI search
  const handleNpiSearchDebounced = (value) => {
    if (npiSearchTimeout.current) clearTimeout(npiSearchTimeout.current);
    npiSearchTimeout.current = setTimeout(() => {
      handleNpiSearchByValue(value);
    }, 400);
  };
  const handleNpiSearchByValue = async (value) => {
    setNpiLoading(true);
    try {
      const results = await npiRegistryService.searchProvidersByName(undefined, value);
      setNpiResults(results);
    } catch (e) {
      setNpiResults([]);
    }
    setNpiLoading(false);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 2 }}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()}>
          Add Member
        </Button>
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Specialty</TableCell>
              <TableCell>Source</TableCell>
              <TableCell>Contact</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {careTeam.map((member, idx) => (
              <TableRow key={idx}>
                <TableCell>{member.name}</TableCell>
                <TableCell>{USER_ROLE_DISPLAY_NAMES[member.role] || member.role}</TableCell>
                <TableCell>{member.specialty}</TableCell>
                <TableCell>
                  {member.source && (
                    <Tooltip title={`Added via ${member.source} method`}>
                      <span style={{ 
                        textTransform: 'capitalize',
                        color: member.source === 'internal' ? '#1976d2' : 
                               member.source === 'external' ? '#ed6c02' : '#2e7d32'
                      }}>
                        {member.source}
                      </span>
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell>{member.phone} {member.email}</TableCell>
                <TableCell>
                  <IconButton size="small" onClick={() => handleOpen(idx)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleDelete(idx)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Dialog
        open={open}
        onClose={(_, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
          handleClose();
        }}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {editIdx !== null ? 'Edit Care Team Member' : 'Add Care Team Member'}
          <IconButton aria-label="close" onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Tabs value={dialogTab} onChange={(_, v) => setDialogTab(v)} sx={{ mb: 2 }}>
            <Tab label="Internal" />
            <Tab label="External" />
            <Tab label="Manual" />
          </Tabs>
          {dialogTab === 0 && (
            <>
              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel>Role</InputLabel>
                <Select
                  name="role"
                  value={form.role}
                  label="Role"
                  onChange={handleChange}
                >
                  {getEnumValues(UserRole).map(role => (
                    <MenuItem key={role} value={role}>{USER_ROLE_DISPLAY_NAMES[role]}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel>User</InputLabel>
                <Select
                  name="userId"
                  value={form.userId}
                  label="User"
                  onChange={handleUserSelect}
                  disabled={!form.role}
                >
                  {filteredUsers.map(user => (
                    <MenuItem key={user.id} value={user.id}>{user.name || user.email}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                margin="dense"
                label="Specialty"
                name="specialty"
                value={form.specialty}
                onChange={handleChange}
                fullWidth
              />
              <TextField
                margin="dense"
                label="Phone"
                name="phone"
                value={form.phone}
                onChange={handleChange}
                fullWidth
              />
              <TextField
                margin="dense"
                label="Email"
                name="email"
                value={form.email}
                onChange={handleChange}
                fullWidth
              />
            </>
          )}
          {dialogTab === 1 && (
            <>
              <NPIProviderSearchField
                onProviderSelect={provider => {
                  setForm({
                    ...form,
                    name: provider.basic
                      ? (provider.basic.first_name
                          ? `${provider.basic.first_name} ${provider.basic.last_name || ''}`.trim()
                          : provider.basic.name || '')
                      : '',
                    specialty: provider.taxonomies?.[0]?.desc || '',
                    phone: provider.addresses?.[0]?.telephone_number || '',
                    email: provider.basic?.authorized_official_telephone_number || '',
                    userId: '',
                    facility: '',
                    source: 'external', // Explicitly set source for NPI providers
                  });
                }}
              />
              <TextField
                margin="dense"
                label="Name"
                name="name"
                value={form.name}
                onChange={handleChange}
                fullWidth
              />
              <TextField
                margin="dense"
                label="Specialty"
                name="specialty"
                value={form.specialty}
                onChange={handleChange}
                fullWidth
              />
              <TextField
                margin="dense"
                label="Phone"
                name="phone"
                value={form.phone}
                onChange={handleChange}
                fullWidth
              />
              <TextField
                margin="dense"
                label="Email"
                name="email"
                value={form.email}
                onChange={handleChange}
                fullWidth
              />
            </>
          )}
          {dialogTab === 2 && (
            <>
              <TextField
                margin="dense"
                label="Name"
                name="name"
                value={form.name}
                onChange={handleChange}
                fullWidth
              />
              <TextField
                margin="dense"
                label="Role"
                name="role"
                value={form.role}
                onChange={handleChange}
                fullWidth
              />
              <TextField
                margin="dense"
                label="Specialty"
                name="specialty"
                value={form.specialty}
                onChange={handleChange}
                fullWidth
              />
              <TextField
                margin="dense"
                label="Phone"
                name="phone"
                value={form.phone}
                onChange={handleChange}
                fullWidth
              />
              <TextField
                margin="dense"
                label="Email"
                name="email"
                value={form.email}
                onChange={handleChange}
                fullWidth
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={loading}>
            {editIdx !== null ? 'Save Changes' : 'Add Member'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
} 